import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { Button, Checkbox, Dropdown, Input, Popover, Tooltip } from 'antd';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';
import defineCanClearTime from '@components/DatePickerComponent/utils/defineCanClearTime';
import { toggleApprovalModal } from '@redux/actions/modalAction';
import { useOrganization } from '@components/OrganizationBoundary';
import { AWStageDto } from '@api/Api';
import defineIsNextStageHasDeadline from '@components/DatePickerComponent/utils/defineIsNextStageHasDeadline';
import DatePickerInput from '@components/DatePickerComponent/DatePickerInput';
import getValidDeadlineDateForStage from '@components/DatePickerComponent/utils/getValidDeadlineDateForStage';
import defineIsPrevStageHasDeadline from '@components/DatePickerComponent/utils/defineIsPrevStageHasDeadline';
import getValidDeadlineTimeForStage from '@components/DatePickerComponent/utils/getValidDeadlineTimeForStage';
import { ReactComponent as DownArrowSvg } from '@assets/icons/arrow-down.svg';
import { ReactComponent as DeleteSvg } from '@assets/icons/bulkActions/delete.svg';
import { ReactComponent as ArrowRightSmallSvg } from '@assets/icons/arrow-right_small.svg';
import { ReactComponent as UsersPlusSvg } from '@assets/icons/users-plus.svg';
import { hashedColor } from '@helpers/hashedColor';

import type { AwStateStage } from '@components/Modals/ApprovalModal/ApprovalModal/ApprovalModal';
import StageMembersModal from '@components/Modals/ApprovalModal/ApprovalModal/components/StagesStep/components/StageMembersModal';
import StageMember from '@components/Modals/ApprovalModal/ApprovalModal/components/StageMember';

import './StageCard.less';
import classNames from 'classnames';

type StageCardProps = {
  stage: AwStateStage;
  stages: AwStateStage[];
  ownerEmail: string;
  campaignId?: string;
  stageNumber: number;
  onUpdateStage: (value: AwStateStage) => void;
  onRemoveStage: (value: AwStateStage) => void;
};

export default function StageCard({
  stage,
  campaignId,
  ownerEmail,
  stages,
  stageNumber,
  onUpdateStage,
  onRemoveStage
}: StageCardProps) {
  const dispatch = useDispatch();
  const isFinishedStage = stage.isFinished && stageNumber !== stages.length;
  const [openStageMembersModal, setOpenStageMembersModal] = useState(false);

  const { currentOrganization } = useOrganization(false);

  const stageMembers = useMemo(() => stage.members || [], [stage.members]);
  const stageColor = useMemo(
    () => hashedColor(`${stageNumber}`, 'taskGroup'),
    [stageNumber]
  );

  const isPrevStageHasDeadline = useMemo(() => {
    const hasPrevStageDeadline = defineIsPrevStageHasDeadline({
      stage: stage as AWStageDto,
      stages: stages as AWStageDto[]
    });
    const hasStageDeadline = !!stage.deadline;
    return hasPrevStageDeadline || hasStageDeadline;
  }, [stage, stages]);

  const isNextStageInPast = useMemo(() => {
    const currentIndex = stages.findIndex((s) => s.id === stage.id);
    const nextStages = stages.slice(currentIndex + 1);
    const nextStageWithDeadline = nextStages.find((s) => s.deadline);
    if (!nextStageWithDeadline) return false;
    return moment(nextStageWithDeadline.deadline)
      .local()
      .isBefore(moment().local());
  }, [stage, stages]);

  const datePickerTooltipText = useMemo(() => {
    if (!isPrevStageHasDeadline) {
      return 'To manage deadline for this stage, you need to set it for previous';
    }
    if (isNextStageInPast) {
      return 'To manage deadline for this stage, you need to update the date of the next';
    }
    return null;
  }, [isPrevStageHasDeadline, isNextStageInPast]);

  const {
    allDecisionsSubmittedChecked,
    oneDecisionSubmittedChecked,
    hasDominatingDecisions
  } = useMemo(() => {
    const withDecision = stageMembers.filter((x) => x.canSubmitDecision);
    const withDominatingDecisions = withDecision.filter(
      (x) => x.hasDominatingDecision
    );
    const allDecisionsSubmittedChecked =
      stage.completionCondition === 'all_decisions_submitted' &&
      (withDominatingDecisions.length === 0 ||
        withDominatingDecisions.length === withDecision.length);
    const oneDecisionSubmittedChecked =
      stage.completionCondition === 'one_decision_submitted' &&
      withDominatingDecisions.length < 2;
    return {
      allDecisionsSubmittedChecked,
      oneDecisionSubmittedChecked,
      hasDominatingDecisions: !!withDominatingDecisions.length
    };
  }, [stageMembers, stage.completionCondition]);

  const stageCompleteConditionLabel = useMemo(() => {
    if (allDecisionsSubmittedChecked) return 'All decisions submitted';
    if (oneDecisionSubmittedChecked) return 'One decision submitted';
    if (stage.completionCondition === 'all_decisions_submitted')
      return 'Selected decisions submitted';
    return 'One of selected decisions submitted';
  }, [
    allDecisionsSubmittedChecked,
    oneDecisionSubmittedChecked,
    stage.completionCondition
  ]);

  const { members, membersPopover } = useMemo(() => {
    return {
      members: stageMembers.slice(0, 6),
      membersPopover: stageMembers.slice(6)
    };
  }, [stageMembers]);

  const onChangeStageName = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      onUpdateStage({
        ...stage,
        name: value
      });
    },
    [stage, onUpdateStage]
  );

  const onChangeDeadline = useCallback(
    (date: string | null) => {
      onUpdateStage({
        ...stage,
        deadline: date
      });
    },
    [stage, onUpdateStage]
  );

  const onChangeStageCompleteCondition = useCallback(
    (condition: 'all_decisions_submitted' | 'one_decision_submitted') => {
      onUpdateStage({
        ...stage,
        completionCondition: condition,
        members: stageMembers.map((x) => {
          return {
            ...x,
            hasDominatingDecision: condition === 'all_decisions_submitted'
          };
        })
      });
    },
    [stage, stageMembers, onUpdateStage]
  );

  const onStageCompleteUserCondition = useCallback(
    ({ value, email }: { value: boolean; email: string }) => {
      const newMembers = stageMembers.map((m) => {
        if (m.email === email) return { ...m, hasDominatingDecision: value };
        return m;
      });
      onUpdateStage({
        ...stage,
        completionCondition:
          newMembers.filter((x) => x.hasDominatingDecision).length < 2
            ? ('one_decision_submitted' as const)
            : ('all_decisions_submitted' as const),
        members: newMembers
      });
    },
    [stage, stageMembers, onUpdateStage]
  );

  const onOpenStageMembersModal = useCallback(() => {
    if (isFinishedStage) return;
    setOpenStageMembersModal(true);
  }, [isFinishedStage, setOpenStageMembersModal]);

  return (
    <div className="stage-card" style={{ borderColor: stageColor }}>
      <div
        className="stage-card_arrow-line"
        style={{ backgroundColor: stageColor }}
      >
        <ArrowRightSmallSvg />
      </div>
      <div className="stage-card_head">
        <div className="stage-card_head-label">Stage {stageNumber}</div>
        <Button
          onClick={() => onRemoveStage(stage)}
          icon={<DeleteSvg />}
          type="text"
          style={{
            padding: 0,
            width: 30,
            height: 30,
            justifyContent: 'center'
          }}
        />
      </div>
      <div id="AW_STAGE_NAME" className="stage-card_input-name">
        <Input
          placeholder="Approval stage name"
          size="large"
          value={stage.name}
          onChange={onChangeStageName}
        />
      </div>
      <div className="stage-card_inputs">
        <div id="AW_STAGE_DEADLINE" className="stage-card_input">
          <div className="stage-card_input-label">Set a deadline</div>
          <UpgradePlanTooltip
            text="to a paid plan to add a due date."
            isCanVisible={
              !currentOrganization?.entity?.features.approvalRoutingDueDates
            }
            beforeUpgradeClick={() => dispatch(toggleApprovalModal(null))}
            innerStyle={{
              transform: 'translate(-60px, 20px)'
            }}
          >
            <Tooltip
              title={datePickerTooltipText}
              overlayClassName="stage-card_tooltip"
              placement="top"
            >
              <div>
                <DatePickerInput
                  placeholder="No deadline"
                  className="deadline-input"
                  placement="leftBottom"
                  disabled={!!datePickerTooltipText}
                  readonly={
                    !currentOrganization?.entity?.features
                      .approvalRoutingDueDates
                  }
                  showTime
                  clearable={
                    !defineIsNextStageHasDeadline({
                      stage: stage as AWStageDto,
                      stages: stages as AWStageDto[]
                    })
                  }
                  clearableTime={defineCanClearTime({
                    stage: stage as AWStageDto,
                    stages: stages as AWStageDto[]
                  })}
                  value={stage.deadline ? moment(stage.deadline) : null}
                  onChange={(value) => {
                    onChangeDeadline(value?.toISOString() ?? null);
                  }}
                  selectedDefaultTime={moment().startOf('day').set({
                    hour: 9,
                    minute: 0,
                    second: 0,
                    millisecond: 0
                  })}
                  timeText="Time due"
                  selectTimeText="Add time due"
                  disabledDate={(v) => {
                    return getValidDeadlineDateForStage({
                      stage: stage as AWStageDto,
                      stages: stages as AWStageDto[],
                      v
                    });
                  }}
                  disabledTime={(v) => {
                    return getValidDeadlineTimeForStage({
                      stage: stage as AWStageDto,
                      stages: stages as AWStageDto[],
                      v
                    });
                  }}
                />
              </div>
            </Tooltip>
          </UpgradePlanTooltip>
        </div>
        <div id="AW_STAGE_COMPLETE_WHEN" className="stage-card_input">
          <div className="stage-card_input-label">Stage is complete when</div>
          <Tooltip
            title={
              isFinishedStage && 'Editing is disabled for a completed stage'
            }
            overlayClassName="stage-card_tooltip"
            placement="top"
          >
            <Dropdown
              overlay={
                <div className="stage-card_dropdown-menu">
                  <Checkbox
                    value="all_decisions_submitted"
                    onChange={() =>
                      onChangeStageCompleteCondition('all_decisions_submitted')
                    }
                    checked={allDecisionsSubmittedChecked}
                  >
                    All decisions submitted
                  </Checkbox>
                  <Checkbox
                    value="one_decision_submitted"
                    onChange={() =>
                      onChangeStageCompleteCondition('one_decision_submitted')
                    }
                    checked={oneDecisionSubmittedChecked}
                  >
                    One decision submitted
                  </Checkbox>
                  {stageMembers.some((x) => x.canSubmitDecision) && (
                    <>
                      <div className="stage-card_dropdown-group-name">
                        Select Reviewers
                      </div>
                      {stageMembers
                        .filter((x) => x.canSubmitDecision)
                        .map((member) => {
                          return (
                            <Checkbox
                              disabled={!member.canSubmitDecision}
                              key={member.email}
                              checked={member.hasDominatingDecision}
                              onChange={(e) =>
                                onStageCompleteUserCondition({
                                  value: e.target.checked,
                                  email: member.email
                                })
                              }
                            >
                              {member.name || member.email}
                            </Checkbox>
                          );
                        })}
                    </>
                  )}
                </div>
              }
              trigger={['click']}
              disabled={isFinishedStage}
            >
              <div
                className={classNames('stage-card_dropdown-label', {
                  'b-disabled': isFinishedStage
                })}
              >
                <div className="stage-card_dropdown-label-text">
                  {stageCompleteConditionLabel}
                </div>
                <DownArrowSvg className="drop-down-arrow" />
              </div>
            </Dropdown>
          </Tooltip>
        </div>
      </div>
      {!!members.length && (
        <div className="stage-card_members">
          {members.map((item) => (
            <div
              key={item.email}
              onClick={onOpenStageMembersModal}
              className="stage-card_members-block"
            >
              <StageMember
                member={item}
                isOwner={item.email === ownerEmail}
                isShowPriorityText={false}
              />
            </div>
          ))}
        </div>
      )}

      <Popover
        overlayClassName="stage-card_popover"
        content={membersPopover.map((item) => (
          <StageMember
            key={item.email}
            member={item}
            isOwner={item.email === ownerEmail}
            isShowPriorityText={false}
          />
        ))}
        placement="topLeft"
        trigger={membersPopover.length ? undefined : []}
      >
        <Tooltip
          title={isFinishedStage && 'Editing is disabled for a completed stage'}
          overlayClassName="stage-card_tooltip"
          placement="bottomLeft"
        >
          <div
            className={classNames('stage-card_add-member', {
              'b-disabled': isFinishedStage
            })}
            onClick={onOpenStageMembersModal}
          >
            <UsersPlusSvg />
            {membersPopover.length
              ? `And ${membersPopover.length} more`
              : 'Add stage reviewers'}
          </div>
        </Tooltip>
      </Popover>
      <StageMembersModal
        isOpen={openStageMembersModal}
        ownerEmail={ownerEmail}
        stageNumber={stageNumber}
        members={stageMembers}
        campaignId={campaignId}
        hasDominatingDecisions={hasDominatingDecisions}
        allDecisionsSubmittedChecked={allDecisionsSubmittedChecked}
        stageFinished={false}
        onCloseModal={() => setOpenStageMembersModal(false)}
        onUpdateMembers={(newMembers) =>
          onUpdateStage({ ...stage, members: newMembers })
        }
      />
    </div>
  );
}
